/* -----------------Globals--------------- */
import { useEffect, useState } from "react";

/* -----------------Types--------------- */
import type { FeedbackParams } from "../feedback/types";
import type { HandleTracking } from "../types";

/* -----------------Child components--------------- */
import FullReportDialog from "./FullReportDialog";
import SessionAnalysisCard from "./SessionAnalysisCard";

/* -----------------Helpers & Hooks--------------- */
import { useSessionAnalysis } from "./hooks/useSessionAnalysis";

/* -----------------Constants--------------- */
import { events } from "./constants";

//@ts-ignore
import packageJson from "../../package.json";
import type { SessionMetadata } from "./types";

type Props = {
	sessionId: string;
	handleTracking: HandleTracking;
	feedbackDialogParams?: FeedbackParams;
	context: string;
	bottomContent?: React.ReactNode;
	shouldShowFullReport?: boolean;
	sessionMetadata?: SessionMetadata;
};
export const SessionAnalysis = ({
	sessionId,
	handleTracking,
	feedbackDialogParams,
	context,
	bottomContent,
	shouldShowFullReport,
	sessionMetadata,
}: Props) => {
	const examName = context;
	const tenantName = feedbackDialogParams?.companyName ?? "";
	const { sessionAnalysis, loading, streaming } = useSessionAnalysis({
		sessionId,
		examName,
		tenantName,
	});

	// const sessionAnalysis = `### Summary\n1. Congratulations on completing the mini-diagnostic assessment for the Hairstylist Theory Exam! \n2. In this assessment, you attempted a total of 20 questions, out of which you answered 7 questions correctly and 13 questions incorrectly.\n3. There were no skipped questions in this session.\n4. The presence of incorrect responses denotes that there is an opportunity for improvement in certain areas to achieve a better score in the actual examination.\n5. This analysis aims to provide valuable insights and recommendations for your continued progress.\n\n### Topics that need improvement\n1. Based on the mini-diagnostic assessment data, improvement is needed in the following topics: Chemical Services, Hair, Scalp, and Skin Analysis, Haircutting, and Hairstyling.\n2. In Chemical Services, you scored 50% with equal numbers of correct and incorrect answers, indicating the need for further practice in this domain.\n3. When it comes to Hair, Scalp, and Skin Analysis, your accuracy is 25%, with one correct answer out of four, suggesting a stronger focus on this topic.\n4. Haircutting is another area of concern as you didn't answer any of the four questions correctly.\n5. In Hairstyling, your average score was 50%, providing another opportunity for growth.\n6. Be mindful that this analysis is based on a subset of data and may not represent your complete performance.\n\n### Recommendations\n1. To improve performance in the identified topics, we recommend dedicating more time to study and practice using our PSI Test Prep study pack and practice tests.\n2. Focus on the topics of Chemical Services, Hair, Scalp, and Skin Analysis, Haircutting, and Hairstyling while utilizing the available resources and comprehensive practice tests.\n3. Revisit key concepts and seek additional learning materials if needed to solidify your understanding.\n4. Consistently taking full-length practice tests can help identify patterns in your performance and enhance your overall test-taking abilities.\n5. Implement strategies to manage time effectively and approach the examination with a clear and focused mindset.\n\n### Key takeaways\n1. This mini-diagnostic assessment has highlighted essential topics for improvement, such as Chemical Services, Hair, Scalp, and Skin Analysis, Haircutting, and Hairstyling.\n2. Utilize the features offered by PSI Test Prep to strengthen your understanding of these topics and enhance your overall performance.\n3. Stay consistent in your preparation efforts and frequently take full-length practice tests to measure your progress.\n4. By following the recommendations provided, you can substantially improve your scores in the actual Hairstylist Theory Exam.`;
	const [showFullReport, setShowFullReport] = useState<boolean | undefined>(
		false,
	);

	useEffect(() => {
		setShowFullReport(shouldShowFullReport);
	}, [shouldShowFullReport]);

	const version = packageJson.version;
	const handleProxyTracking = (
		eventName: string,
		params?: Record<string, string>,
		increment?: boolean,
	) => {
		const additionalParams = {
			context,
			version,
			feature: "AI_SESSION_ANALYSIS",
		};
		handleTracking(
			eventName,
			{
				...(params || {}),
				...additionalParams,
			},
			increment,
		);
	};
	const toggleShowCompleteReport = () => {
		if (!showFullReport) {
			handleProxyTracking(
				events.viewReportClicked,
				{
					sessionId,
				},
				true,
			);
		}
		setShowFullReport((prev) => !prev);
	};
	return (
		<>
			<SessionAnalysisCard
				sessionAnalysis={sessionAnalysis}
				loading={loading}
				toggleShowCompleteReport={toggleShowCompleteReport}
			/>
			{showFullReport && (
				<FullReportDialog
					visible={showFullReport}
					handleDismiss={toggleShowCompleteReport}
					sessionAnalysisData={sessionAnalysis}
					handleTracking={handleProxyTracking}
					feedbackDialogParams={feedbackDialogParams}
					bottomContent={bottomContent}
					sessionMetadata={sessionMetadata}
					streaming={streaming}
				/>
			)}
		</>
	);
};
